import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    AppBar,
    Toolbar,
    Typography,
    Box,
    TextField,
    IconButton,
    Avatar,
    Stack,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import LogoutIcon from '@mui/icons-material/Logout';
import { styled } from '@mui/material/styles';

import Message from '../components/Message'; 
import { useAuth } from '../context/AuthContext';
// Assuming these API calls are defined elsewhere
import { startChat, sendMessage, getChatHistory, logoutUser } from '../api/api'; 

// --- Elevated Flat Brand Palette (Fresher Version) ---
const BRAND_CTA_ACCENT = '#ff8c37'; // Brighter Orange/Accent
const BRAND_PRIMARY_TEXT = '#0b5295'; // Slightly lighter, richer Navy Blue
const BRAND_HOVER_COLOR = '#073a6a'; // Darker Blue
const UI_CANVAS_BACKGROUND = '#fcfcfc'; // Whiter screen background
const UI_SHADOW_TOP = '0 -2px 8px rgba(0, 0, 0, 0.08)'; // Lighter, modern shadow

// Max width for chat content on desktop 
const CHAT_MAX_WIDTH = '800px'; 

// Styled Components
const ChatContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    height: '100vh', 
    overflow: 'hidden',
    backgroundColor: UI_CANVAS_BACKGROUND,
}));

// Messages Area is the scrollable wrapper
const MessagesArea = styled(Box)(({ theme }) => ({
    flexGrow: 1,
    overflowY: 'auto',
    padding: theme.spacing(0, { xs: 0, md: 5 }), 
    display: 'flex',
    flexDirection: 'column',
    scrollBehavior: 'smooth',
    backgroundColor: UI_CANVAS_BACKGROUND, 
}));

// Input Area is fixed at the bottom
const InputArea = styled(Box)(({ theme }) => ({
    padding: theme.spacing(1.5, 0),
    backgroundColor: theme.palette.common.white, 
    boxShadow: UI_SHADOW_TOP, 
    display: 'flex',
    justifyContent: 'center',
    // Mobile optimization: Use padding 1 on X axis for slight inset
    [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(1, 1),
    },
}));

// Wrapper to center the input field and button
const InputWrapper = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'flex-end', // Aligns the input (which has helper text) and the button
    width: '100%',
    maxWidth: CHAT_MAX_WIDTH,
    padding: theme.spacing(0, 2), 
    [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(0, 0),
    },
}));

// Typing Indicator Component (Visual feedback refinement)
const TypingIndicator = () => (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: 1, mb: 1, color: BRAND_PRIMARY_TEXT }}>
        <CircularProgress size={12} thickness={5} />
        <Typography variant="caption" sx={{ fontStyle: 'italic', color: BRAND_PRIMARY_TEXT }}>
            AI is thinking...
        </Typography>
    </Stack>
);

// --- INPUT VALIDATION CONSTANTS & HELPERS (Frontend UX Layer) ---
const MAX_WORDS = 100; // Increased limit to be more reasonable for a chat interface

// 1. Simple, exposed list of forbidden words (Frontend UX check only)
// Note: This list is highly incomplete and must be fully enforced on the Backend.
const FORBIDDEN_WORDS = ['password', 'steal', 'key', 'secret', 'confidential', 'account number', 'reveal'];

// 2. Simple, exposed regex patterns for common jailbreak attempts (Frontend UX check only)
const JAILBREAK_PATTERNS = [
    /ignore previous instructions/i,
    /ignore all previous instructions/i,
    /i am admin/i,
    /i am administrator/i,
    /reveal the key/i,
    /developer mode/i
];

// 3. Simple PII patterns (discouragement/UX check)
const PII_PATTERNS = [
    /\b\d{3}[- ]?\d{2}[- ]?\d{4}\b/g, // Potential SSN (XXX-XX-XXXX)
    /\b(\d{4}[- ]){3}\d{4}\b/g, // Potential Credit Card (XXXX-XXXX-XXXX-XXXX)
    /\b(\d{10,12})\b/g, // Potential Phone or long ID number
];

const countWords = (text) => {
    if (!text) return 0;
    // Split by whitespace characters, filter out empty strings
    return text.trim().split(/\s+/).filter(Boolean).length;
};

// Core Frontend Validation Function
const validateInput = (text) => {
    const message = text.trim().toLowerCase();

    if (message.length === 0) {
        return 'Message cannot be empty.';
    }
    // 1. Forbidden Word Check
    for (const word of FORBIDDEN_WORDS) {
        if (message.includes(word)) {
            // Display the specific word found for clearer feedback
            const foundWord = message.match(new RegExp(word, 'i'))[0]; 
            return `Input contains a sensitive word ("${foundWord}"). Please remove or rephrase your query.`;
        }
    }
    // 2. Simple Jailbreak/Injection Check (Regex)
    for (const pattern of JAILBREAK_PATTERNS) {
        if (pattern.test(message)) {
            return `Unauthorized commands detected (e.g., "ignore" or "admin"). Please use standard conversational language.`;
        }
    }
    // 3. Simple PII Check (Discouragement)
    for (const pattern of PII_PATTERNS) {
        if (pattern.test(message)) {
            return `Please avoid entering sensitive personal information (PII) like IDs or account numbers. This data will be blocked for security.`;
        }
    }
    // 4. Word Count Check
    if (countWords(text) > MAX_WORDS) {
        return `Your message exceeds the maximum limit of ${MAX_WORDS} words.`;
    }

    return null; // Input is valid
};
// ---------------------------------------------


const ChatPage = () => {
    const { user, session: sessionId, logout, appNumber } = useAuth(); 
    const [messages, setMessages] = useState([]);
    const [inputText, setInputText] = useState('');
    const [isSendingMessage, setIsSendingMessage] = useState(false);
    const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
    const messagesEndRef = useRef(null);
    const [chatInitiated, setChatInitiated] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    
    // NEW: State for validation errors
    const [inputError, setInputError] = useState(''); 

    const scrollToBottom = useCallback(() => {
        // Use a slightly longer timeout to ensure content reflow (especially for dynamic markdown)
        setTimeout(() => {
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }, 150); 
    }, []);

    // --- EFFECT HOOKS ---
    useEffect(() => {
        if (user && sessionId) {
            setInitialLoading(true);
            const fetchHistory = async () => {
                try {
                    // const history = await getChatHistory(user.ntid, sessionId);
                    // setMessages(history.messages || []);
                } catch (error) {
                    console.error('Failed to fetch chat history:', error);
                    setMessages([]);
                } finally {
                    setInitialLoading(false);
                }
            };
            fetchHistory();
        } else {
            setMessages([]);
            setInitialLoading(false);
        }
    }, [user, sessionId]);
    useEffect(() => {
        // Now, user, sessionId, and appNumber are GUARANTEED to exist!
        if (user && sessionId && !chatInitiated && !initialLoading) {
            const initializeChat = async () => {
                try {
                    // This will now successfully fire!
                    const response = await startChat(sessionId, user.ntid, appNumber);
                    setChatInitiated(true);
                    
                    if (response && response.initial_message) {
                        setMessages((prev) => [
                            ...prev, 
                            { sender: 'assistant', content: response.initial_message }
                        ]);
                    }
                } catch (error) {
                    console.error('Failed to start chat:', error);
                }
            };
            initializeChat();
        }
    }, [user, sessionId, chatInitiated, initialLoading, appNumber]);

    useEffect(() => {
        scrollToBottom();
    }, [messages, isSendingMessage, scrollToBottom]);


    // --- Input Change Handler ---
    const handleInputChange = (event) => {
        const rawText = event.target.value;
        setInputText(rawText);
    
       const validationResult = validateInput(rawText);
        
        // Only clear the error if the current input text passes validation checks
        if (!validationResult) {
            setInputError('');
        }
    };

    // --- HANDLE SEND MESSAGE (Frontend Validation Check) ---
    const handleSendMessage = async () => {
        const messageToSend = inputText.trim();
        // 0. Pre-send validation check (disabled button should prevent this, but check anyway)
        if (messageToSend === '' || isSendingMessage || !user || !sessionId) {
            return;
        }
        // 1. RUN CRITICAL FRONTEND VALIDATION
        const validationResult = validateInput(messageToSend);
        if (validationResult) {
            // If validation fails, set the error state and stop execution
            setInputError(validationResult);
            return;
        }
        // Validation passed, ensure error state is clear
        setInputError('');

        const newUserMessage = { sender: 'user', content: messageToSend };
        
        // 2. Add user message
        setMessages((prevMessages) => [...prevMessages, newUserMessage]);
        setInputText(''); // Clear input box
        
        // 3. Activate typing indicator
        setIsSendingMessage(true); 

        try {
            const response = await sendMessage(user.ntid, sessionId, appNumber, messageToSend);
            const assistantMessage = { sender: 'assistant', content: response.response };
            
            // 4. Add assistant message
            setMessages((prevMessages) => [...prevMessages, assistantMessage]);

        } catch (error) {
            console.error('Failed to send message:', error);
            const errorMessage = {
                sender: 'system',
                content: error.message || 'Sorry, I encountered an error. Please try again.',
            };
            setMessages((prevMessages) => [...prevMessages, errorMessage]);
        } finally {
            setIsSendingMessage(false);
        }
    };

    const handleKeyPress = (event) => {
        // Prevent sending if shift+enter (for multiline) or if validation failed
        if (event.key === 'Enter' && !event.shiftKey && inputText.trim() !== '') {
            event.preventDefault();
            handleSendMessage();
        }
    };

    // ... (Logout handlers and other helpers remain unchanged) ...
    const handleLogoutClick = () => setShowLogoutConfirm(true);
    const handleCancelLogout = () => setShowLogoutConfirm(false);
    
    const handleConfirmLogout = async () => {
        try {
            await logoutUser(user.ntid, sessionId);
        } catch (error) {
            console.error('Logout API call failed:', error);
        } finally {
            logout();
            setShowLogoutConfirm(false);
        }
    };

    const getAvatarProps = (sender) => {
        const isUser = sender === 'user';
        const initial = isUser ? user?.ntid.charAt(0).toUpperCase() : 'AI'; 
        const color = isUser ? BRAND_PRIMARY_TEXT : BRAND_CTA_ACCENT; 
        return { initial, color, isUser };
    };

    const modernInputTextFieldStyle = {
        '& .MuiOutlinedInput-root': {
            backgroundColor: UI_CANVAS_BACKGROUND, 
            borderRadius: '25px', 
            alignItems: 'flex-start',
            '&.Mui-focused fieldset': { 
                borderColor: BRAND_PRIMARY_TEXT, 
                borderWidth: '2px',
            },
        },
        '& .MuiInputBase-input': { 
            color: BRAND_PRIMARY_TEXT, 
            paddingY: 1.5, 
            paddingX: 2,
            lineHeight: 1.5, 
        },
        px: { xs: 0, sm: 0 },
    };

    const modernSendButtonStyle = {
        backgroundColor: BRAND_CTA_ACCENT,
        color: 'white',
        width: 48, 
        height: 48,
        minWidth: 48,
        borderRadius: '50%',
        boxShadow: '0 4px 8px rgba(0,0,0,0.2)', 
        '&:hover': {
            backgroundColor: BRAND_HOVER_COLOR, 
        },
        '&:disabled': {
            backgroundColor: '#ccc', 
            color: '#999',
        }
    };


    return (
        <ChatContainer>
            
            {/* --- AppBar (Sticky Header) --- */}
            <AppBar 
                position="sticky" 
                sx={{ 
                    bgcolor: BRAND_PRIMARY_TEXT,
                    boxShadow: UI_SHADOW_TOP, 
                }}
            >
                <Toolbar>
                    {/* Left: Title and User Info */}
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ flexGrow: 1 }}>
                        <Avatar sx={{ bgcolor: BRAND_CTA_ACCENT, width: 32, height: 32 }}>
                            {user?.ntid.charAt(0).toUpperCase() || '?'}
                        </Avatar>
                        <Typography variant="subtitle1" component="div" sx={{ fontWeight: 500 }}>
                            InstaTrack Chat
                        </Typography>
                        {/* Responsive NTID: Visible only on wider screens */}
                        <Typography 
                            variant="caption" 
                            sx={{ opacity: 0.8, display: { xs: 'none', sm: 'block' } }}
                        >
                            ({user?.ntid})
                        </Typography>
                    </Stack>
                    
                    {/* Right: Logout Button */}
                    <IconButton color="inherit" onClick={handleLogoutClick}>
                        <LogoutIcon />
                    </IconButton>
                </Toolbar>
            </AppBar>

            <MessagesArea>
                <Box 
                    sx={{ 
                        maxWidth: CHAT_MAX_WIDTH, 
                        width: '100%', 
                        mx: 'auto', 
                        pt: 4, 
                        pb: 2,
                        px: { xs: 2, md: 0 } 
                    }}
                >
                    {initialLoading || (!chatInitiated && messages.length === 0) ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'calc(100vh - 120px)' }}>
                            <CircularProgress sx={{ color: BRAND_PRIMARY_TEXT }}/>
                        </Box>
                    ) : messages.length === 0 ? (
                        <Typography variant="body2" color="textSecondary" align="center" sx={{ mt: 4 }}>
                            Start the conversation!
                        </Typography>
                    ) : (
                        messages.map((msg, index) => {
                            const { initial, color, isUser } = getAvatarProps(msg.sender);
                            
                            return (
                                <Stack
                                    key={index}
                                    direction="row"
                                    spacing={1}
                                    alignItems="flex-start" 
                                    justifyContent={isUser ? 'flex-end' : 'flex-start'} 
                                    sx={{ width: '100%', mb: 1.5 }}
                                >
                                    {/* Avatar (Assistant Left) */}
                                    {!isUser && (
                                        <Avatar sx={{ width: 32, height: 32, bgcolor: color, color: 'white', mt: 0.5 }}>
                                            {initial}
                                        </Avatar>
                                    )}
                                    <Box sx={{ maxWidth: { xs: '85%', sm: '75%' } }}>
                                        <Message message={msg.content} sender={msg.sender} />
                                    </Box>

                                    {/* Avatar (User Right) */}
                                    {isUser && (
                                        <Avatar sx={{ width: 32, height: 32, bgcolor: color, color: 'white', mt: 0.5 }}>
                                            {initial}
                                        </Avatar>
                                    )}
                                </Stack>
                            );
                        })
                    )}
                    
                    {/* Typing Indicator */}
                    {isSendingMessage && (
                            <Stack
                                direction="row"
                                justifyContent="flex-start"
                                sx={{ width: '100%', mb: 1.5 }}
                            >
                                <Box sx={{ maxWidth: { xs: '85%', sm: '75%' } }}>
                                    <TypingIndicator />
                                </Box>
                            </Stack>
                    )}

                    <Box ref={messagesEndRef} sx={{ height: 0 }} /> 
                </Box>
            </MessagesArea>

            <InputArea>
                <InputWrapper>
                    {/* Wrapper for TextField to handle helperText positioning */}
                    <Box sx={{ flexGrow: 1 }}> 
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Type your message..."
                            value={inputText}
                            onChange={handleInputChange} 
                            onKeyPress={handleKeyPress}
                            disabled={isSendingMessage || !chatInitiated}
                            multiline
                            maxRows={4} 
                            error={!!inputError} // Material UI error state uses boolean inputError
                            helperText={inputError || `${countWords(inputText)}/${MAX_WORDS} words`} // Display error or word count
                            sx={{ mr: 1, ...modernInputTextFieldStyle }} 
                        />
                    </Box>
                    <IconButton
                        onClick={handleSendMessage}
                        // Disable if sending, not initiated, empty, OR if there's a validation error
                        disabled={isSendingMessage || !chatInitiated || inputText.trim() === '' || !!inputError} 
                        sx={modernSendButtonStyle} 
                    >
                        {isSendingMessage ? <CircularProgress size={24} sx={{ color: 'white' }} /> : <SendIcon />}
                    </IconButton>
                </InputWrapper>
            </InputArea>

            {/* Logout Dialog */}
            <Dialog
                open={showLogoutConfirm}
                onClose={handleCancelLogout}
                aria-labelledby="logout-dialog-title"
                PaperProps={{ sx: { borderRadius: 2, backgroundColor: 'white' } }}
            >
                <DialogTitle id="logout-dialog-title" sx={{ fontWeight: 600, color: BRAND_PRIMARY_TEXT }}>Confirm Logout</DialogTitle>
                <DialogContent>
                    <DialogContentText id="logout-dialog-description" sx={{ color: BRAND_PRIMARY_TEXT }}>
                        Are you sure you want to log out?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelLogout} sx={{ borderRadius: 2, color: BRAND_PRIMARY_TEXT }}>
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleConfirmLogout} 
                        variant="contained" 
                        sx={{ 
                            borderRadius: 2, 
                            bgcolor: BRAND_PRIMARY_TEXT, 
                            '&:hover': { bgcolor: BRAND_HOVER_COLOR } 
                        }}
                    >
                        Logout
                    </Button>
                </DialogActions>
            </Dialog>
        </ChatContainer>
    );
};

export default ChatPage;